import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BankAccountBase } from '../../../../../common/widgets/payment-update-widget/js/common/forms/bank-account/bank-account-base';
import { PaymentService } from '../../../../../common/widgets/payment-update-widget/js/common/service/payment.service';
import { ModalRouteService } from '../../../../../common/widgets/payment-update-widget/js/common/service/modal-route.service';
import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { AlertIntent, RadioOption } from '@hit/coverpath-design-system';
import { BasicTextInputComponent } from '../../form/base-components/base-text-input/basic-text-input.component';
import { SelectBasicComponent } from '../../form/base-components/select-basic/select-basic.component';
import { PaymentInfoPayload } from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { Utils } from '../../../../../common/js/Utils';
import { Product } from '../../../../../common/js/static/Product';

@Component({
  selector: 'authorize-payment-bank-account-form',
  templateUrl: './authorize-payment-bank-account-form.component.html',
})
export class AuthorizePaymentBankAccountForm extends BankAccountBase {
  @ViewChild('accountNumberInput') accountNumberInput: BasicTextInputComponent;
  @ViewChild('routingNumberInput') routingNumberInput: BasicTextInputComponent;
  @ViewChild('accountTypeSelect') accountTypeSelect: SelectBasicComponent;
  @Input() payerName: string;
  @Input() policyNumber: string;
  @Input() useSecondary: boolean;
  @Input() effectiveDraftDate: string;
  @Input() totalPremium: string;
  @Input() accountType: string;
  @Input() bankAccountLastFour: string;
  @Input() invoiceId: string;
  @Input() hasPaymentFailed: boolean;
  @Input() isAnnualPayment: boolean;
  @Input() isAutoPay: boolean;
  @Input() isBillingPaused: boolean;
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  alertIntent = AlertIntent;
  accountTypeOptions: any[] = [
    { value: 'personal checking', description: 'Checking' },
    { value: 'personal savings', description: 'Savings' },
    { value: 'business checking', description: 'Business Checking' },
    { value: 'business savings', description: 'Business Savings' },
  ];
  radioOptions: RadioOption[];
  authorizationDate: string;
  termsPDF: string;

  constructor(
    paymentService: PaymentService,
    fb: FormBuilder,
    modalRouteService: ModalRouteService,
    amorphicService: AmorphicService,
    private cdRef: ChangeDetectorRef
  ) {
    super(paymentService, fb, modalRouteService, amorphicService);
    const channelName = this.paymentService.getChannelName();

    switch (channelName) {
      case Product.channels.CAS.name:
      case Product.channels.MMOPM.name:
      case Product.channels.MMTRAD.name:
        this.termsPDF = 'premium_payment_services_terms_conditions_cpath.pdf';
        break;
      case Product.channels.MMDirect.name:
        this.termsPDF = 'premium_payment_services_terms_conditions_mmdirect.pdf';
        break;
      default:
        throw Error(`No Terms and Conditions found for channel ${this.paymentService.getChannelName()}`);
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.radioOptions = !this.bankAccountLastFour
      ? [{ label: 'New bank account', value: 'true' }]
      : [
          { label: `${this.accountType} account ending in ${this.bankAccountLastFour}`, value: 'false' },
          { label: 'New bank account', value: 'true' },
        ];

    this.paymentForm.addControl('paymentAuthorized', this.fb.control('', Validators.requiredTrue));
    this.paymentForm.get('paymentAuthorized').setValue(false);
    this.paymentForm.addControl('newBankAccount', this.fb.control('', Validators.required));
    this.paymentForm.get('newBankAccount').setValue(this.radioOptions[0].value);
    this.paymentForm.get('newBankAccount').valueChanges.subscribe((newBankAccountSelected) => {
      if (newBankAccountSelected === 'false') {
        this.paymentForm.get('accountNumber').disable();
        this.paymentForm.get('routingNumber').disable();
        this.paymentForm.get('accountType').disable();
      } else {
        this.paymentForm.get('accountNumber').enable();
        this.paymentForm.get('routingNumber').enable();
        this.paymentForm.get('accountType').enable();
      }
    });
    if (this.hasPaymentFailed) {
      this.paymentForm.get('paymentAuthorized').disable();
    }
    this.authorizationDate = Utils.formatDateLongWithShortMonth(new Date());
  }

  ngAfterViewInit() {
    const accountInputElement: HTMLElement = document.querySelector(`[id*="${this.radioOptions[0].label}"]`);
    accountInputElement.click();
    this.cdRef.detectChanges();
  }

  async validateAN(): Promise<void> {
    super.validateAN();
    if (!!this.anError) {
      this.accountNumberInput.setErrorMessage(this.anError);
    } else {
      this.accountNumberInput.isError = false;
      this.accountNumberInput.errorMessage['accountNumber'] = null;
    }
  }

  async validateRN(): Promise<void> {
    super.validateRN();
    if (!!this.rnError) {
      this.routingNumberInput.setErrorMessage(this.rnError);
    } else {
      this.routingNumberInput.isError = false;
      this.routingNumberInput.errorMessage['routingNumber'] = null;
    }
  }

  validateAccountType() {
    if (this.paymentForm.get('accountType').errors) {
      this.accountTypeSelect.isError = true;
      this.accountTypeSelect.errorMessage['accountTypeComponent'] = 'Account Type is Required.';
    } else {
      this.accountTypeSelect.isError = false;
      this.accountTypeSelect.errorMessage['accountTypeComponent'] = null;
    }
  }

  validatePaymentAuthorized() {
    const authorizeControl = this.paymentForm.get('paymentAuthorized');
    if (authorizeControl.errors) {
      authorizeControl.markAsTouched();
      authorizeControl.updateValueAndValidity();
    }
  }

  updateBankInfoAndAuthorization() {
    const newBankingInfoAdded = this.paymentForm.get('newBankAccount').value === 'true';
    let paymentInfo: PaymentInfoPayload;

    if (newBankingInfoAdded) {
      paymentInfo = {
        paymentInfo: {
          payerName: this.payerName,
          bankAccountNumber: this.paymentForm.get('accountNumber').value,
          bankRoutingNumber: this.paymentForm.get('routingNumber').value,
          accountType: this.paymentForm.get('accountType').value,
          effectiveDraftDate: this.effectiveDraftDate,
        },
      };
    }
    if (this.hasPaymentFailed && !newBankingInfoAdded) {
      this.paymentService
        .retryPaymentAuthorization(this.policyNumber, this.invoiceId, this.useSecondary)
        .subscribe((res) => {
          this.updateComplete.emit(true);
        });
    } else {
      this.paymentService
        .updatePaymentInfoAndAuthorization(
          true,
          this.policyNumber,
          this.useSecondary,
          this.isAutoPay,
          this.isBillingPaused,
          paymentInfo
        )
        .subscribe((res) => {
          this.updateComplete.emit(true);
        });
    }
  }

  isDisabled(): boolean {
    const newBankingInfoAdded = this.paymentForm.get('newBankAccount').value === 'true';
    if (newBankingInfoAdded && this.paymentForm.valid && this.isGiactWebDebitValid) {
      return false;
    } else if (!newBankingInfoAdded && (this.hasPaymentFailed || this.paymentForm.valid)) {
      return false;
    } else {
      return true;
    }
  }

  submit() {
    this.updateBankInfoAndAuthorization();
  }

  cancel() {
    this.paymentService.resetGiactResponse();
    this.closeModal.emit();
  }
}
